import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { trackEvent, trackJoinTeamSubmit } from '../components/GoogleAnalytics';

const GHL_FORM_ID = 'FW3X2NqrPmX3ewvbVypa';
const GHL_IFRAME_ID = `inline-${GHL_FORM_ID}`;

interface JoinTheTeamDialogProps {
  open: boolean;
  onClose: () => void;
}

// GoHighLevel embedded form URL
const GHL_FORM_URL = `https://api.leadconnectorhq.com/widget/form/${GHL_FORM_ID}`;

/**
 * JoinTheTeamDialog - Displays a GoHighLevel embedded form for volunteer sign-up.
 *
 * The form submits directly to GoHighLevel CRM, which handles:
 * - Form validation
 * - Contact creation
 * - Workflow automation (tagging, welcome emails)
 * - Success messaging
 *
 * @param open - Whether the dialog is visible
 * @param onClose - Callback when dialog should close
 */
const JoinTheTeamDialog: React.FC<JoinTheTeamDialogProps> = ({ open, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setHasError(false);
      setIsSubmitted(false);
      trackEvent('join_team_dialog_view', 'forms');
    }
  }, [open]);

  // GHL initialization protocol — respond to the iframe's "fetch-query-params"
  // handshake so the form fully activates before the user submits.
  // form_embed.js is supposed to do this, but we handle it ourselves so we
  // don't need to inject that script (which would conflict with React's DOM).
  useEffect(() => {
    if (!open) return;

    const handleGHLInit = (event: MessageEvent) => {
      if (!Array.isArray(event.data) || event.data[0] !== 'fetch-query-params') return;

      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;

      // Mirror what form_embed.js sends back: query params, current URL,
      // referrer, and the iframe's layout ID.
      iframe.contentWindow.postMessage(
        ['query-params', {}, window.location.href, document.referrer, GHL_IFRAME_ID],
        '*',
      );
    };

    window.addEventListener('message', handleGHLInit);
    return () => window.removeEventListener('message', handleGHLInit);
  }, [open]);

  // Detect GoHighLevel submission events from the embedded iframe so we can
  // show a consistent confirmation view across desktop and mobile.
  useEffect(() => {
    if (!open) return;

    const handleMessage = (event: MessageEvent) => {
      // Accept messages from any known GHL domain
      const knownGHLDomains = ['leadconnectorhq.com', 'msgsndr.com', 'gohighlevel.com'];
      const isFromGHL = knownGHLDomains.some((domain) => event.origin.includes(domain));

      if (!isFromGHL) return;

      const payload =
        typeof event.data === 'string' ? event.data.toLowerCase() : JSON.stringify(event.data ?? '').toLowerCase();

      // Detect any submission success signal from GHL.
      const isSubmittedEvent =
        payload.includes('form submitted') ||
        payload.includes('form_submitted') ||
        payload.includes('formsubmitted') ||
        payload.includes('submission_success') ||
        payload.includes('submitted') ||
        payload.includes('thank you') ||
        payload.includes('thankyou');

      if (isSubmittedEvent) {
        setIsSubmitted(true);
        setIsLoading(false);
        trackJoinTeamSubmit(true);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
    trackJoinTeamSubmit(false);
    trackEvent('join_team_form_load_error', 'forms');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="sm"
      // Prevent MUI from setting body{overflow:hidden}. On Android Chrome, the
      // body scroll-lock breaks touch-event routing into cross-origin iframes
      // inside fullScreen dialogs. Mobile fullScreen covers the whole screen
      // anyway so there's nothing behind it to scroll.
      disableScrollLock
      PaperProps={{
        sx: {
          ...(isMobile
            ? {
                m: 0,
                width: '100%',
                height: '100%',
                maxWidth: '100%',
                maxHeight: '100%',
                borderRadius: 0,
                display: 'flex',
                flexDirection: 'column',
              }
            : { minHeight: 600, maxHeight: '90vh' }),
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, pr: 6, flexShrink: 0 }}>
        Join the Movement
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {/* overflow:auto lets the content scroll if needed and, critically, does
          NOT clip the iframe's touch-event area on mobile (overflow:hidden was
          the prior culprit). flex:1 + minHeight:0 fills remaining Paper height
          without relying on calc(100vh - Npx). */}
      <DialogContent sx={{ p: 0, overflow: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {isSubmitted ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
              p: 4,
              textAlign: 'center',
              gap: 2,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Thanks for joining the movement.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520 }}>
              Your form was submitted successfully. We are excited to have you on board as we build momentum for real
              family court reform.
            </Typography>
            <Button variant="contained" onClick={handleClose} sx={{ mt: 1 }}>
              Close
            </Button>
          </Box>
        ) : hasError ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" color="error" gutterBottom>
              Unable to load form
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please try again later or contact us at info@robertgarza.us
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              // On mobile use flex to fill available height rather than
              // calc(100vh - Npx) — "100vh" on Android Chrome includes the
              // browser chrome, so the calculated size exceeds the visible
              // area and the bottom of the iframe gets clipped by overflow,
              // making the submit button unresponsive to touch.
              ...(isMobile ? { flex: 1, minHeight: 0 } : { height: 735 }),
            }}
          >
            {isLoading && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'absolute',
                  inset: 0,
                  bgcolor: 'background.paper',
                  zIndex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <iframe
              ref={iframeRef}
              src={GHL_FORM_URL}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: '3px',
              }}
              allow="clipboard-write; fullscreen"
              id={GHL_IFRAME_ID}
              data-layout="{'id':'INLINE'}"
              data-trigger-type="alwaysShow"
              data-trigger-value=""
              data-activation-type="alwaysActivated"
              data-activation-value=""
              data-deactivation-type="neverDeactivate"
              data-deactivation-value=""
              data-form-name="JoinTheMovement"
              data-height="735"
              data-layout-iframe-id={GHL_IFRAME_ID}
              data-form-id={GHL_FORM_ID}
              title="Join the Movement Form"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default JoinTheTeamDialog;
